import { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  Chip,
  Avatar,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Collapse,
  alpha,
} from "@mui/material";
import {
  Download,
  People as Users,
  CheckCircle,
  Add,
  Code,
  Check,
  ContentCopy,
} from "@mui/icons-material";
import {
  StyledTabs,
  StyledTab,
  CustomTabPanel2,
  a11yProps,
} from "../../Common/UI/TabPanel";
import type {
  CohortMatchType,
  CohortParticipant,
  CohortPreviewLoadingMode,
  CohortPreviewResult,
} from "./types";
import AddToStudyModal from "./AddToStudyModal";
import AddToCohortModal from "./AddToCohortModal";
import { VirtualizedParticipantList } from "./VirtualizedParticipantList";
import { toastFn } from "../../../utils/toastfn";

interface PreviewResultsProps {
  previewResults: CohortPreviewResult | null;
  loadingMode: CohortPreviewLoadingMode | null;
  onExport: (selectedIds?: string[]) => void;
  onLoadMore: (matchType: CohortMatchType, cursor?: string) => void;
  onParticipantClick: (participantId: string) => void;
  type: "registry" | "study";
  cohortName?: string;
  studyId?: string;
  groupId?: string;
}

const PreviewHeader = ({
  count,
}: {
  count: number;
}) => {
  return (
    <Card
      sx={{
        p: 1.5,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
        borderRadius: "12px",
        border: 1,
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
        boxShadow: "none",
        mb: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: "primary.main",
            boxShadow: "0px 4px 12px rgba(135, 46, 229, 0.3)",
          }}
        >
          <Users sx={{ fontSize: 18 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.25,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: "primary.main",
              }}
              data-testid="text-participant-count"
            >
              {count}
            </Typography>
            {/* {previewResults.delta !== undefined &&
              previewResults.delta !== null && (
                <Chip
                  label={`${previewResults.delta > 0 ? "+" : ""}${
                    previewResults.delta
                  }`}
                  size="small"
                  sx={{
                    bgcolor:
                      previewResults.delta > 0
                        ? "#10b981"
                        : previewResults.delta < 0
                        ? "#ef4444"
                        : "text.secondary",
                    color:
                      previewResults.delta !== 0 ? "#ffffff" : "text.primary",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                    height: "24px",
                    borderRadius: "12px",
                  }}
                  data-testid="badge-delta"
                />
              )} */}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontWeight: "regular",
              }}
            >
              Participants
            </Typography>
            {/* {previewResults.durationMs && (
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 400,
                  opacity: 0.7,
                }}
              >
                • {previewResults.durationMs}ms
              </Typography>
            )} */}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const PreviewSQL = ({ sql, type }: { sql: string; type: string }) => {
  const [showSql, setShowSql] = useState(false);
  const [copiedSql, setCopiedSql] = useState(false);

  // Normalize SQL formatting by removing leading whitespace from each line
  const normalizedSql = useMemo(
    () =>
      sql
        .split("\n")
        .map((line) => line.trimStart())
        .join("\n")
        .trim(),
    [sql]
  );

  const handleCopySql = () => {
    navigator.clipboard.writeText(normalizedSql);
    setCopiedSql(true);
    toastFn({
      title: "SQL Copied",
      description: "Query copied to clipboard",
      variant: "success",
    });
    setTimeout(() => setCopiedSql(false), 2000);
  };

  return (
    <Card
      sx={{
        p: 2,
        mb: 3,
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
        borderRadius: "12px",
        border: 1,
        borderColor: "divider",
      }}
    >
      <Box
        component="button"
        onClick={() => setShowSql(!showSql)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          background: "none",
          border: "none",
          cursor: "pointer",
          p: 0,
        }}
        data-testid="button-toggle-sql"
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Code sx={{ width: 16, height: 16, color: "primary.main" }} />
          <Typography
            variant="body2"
            fontWeight={"medium"}
            sx={{ color: "text.primary" }}
          >
            Generated SQL Query - {type}
          </Typography>
        </Box>
        <Chip
          label={showSql ? "Hide" : "Show"}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.75rem",
            height: "24px",
            borderRadius: "6px",
          }}
        />
      </Box>
      <Collapse in={showSql}>
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                maxHeight: "400px",
                overflowY: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: "8px",
                bgcolor: "background.default",
              }}
            >
              <Box
                component="pre"
                sx={{
                  p: 2,
                  fontSize: "0.75rem",
                  fontFamily: "monospace",
                  m: 0,
                }}
              >
                <Box
                  component="code"
                  sx={{
                    color: "text.primary",
                    whiteSpace: "pre",
                  }}
                >
                  {normalizedSql}
                </Box>
              </Box>
            </Box>
            <IconButton
              size="small"
              onClick={handleCopySql}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "background.paper",
                "&:hover": {
                  bgcolor: "background.paper",
                },
              }}
              data-testid="button-copy-sql"
            >
              {copiedSql ? (
                <Check sx={{ width: 16, height: 16, color: "#10b981" }} />
              ) : (
                <ContentCopy sx={{ width: 16, height: 16 }} />
              )}
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              fontSize: "0.75rem",
            }}
          >
            This SQL query is automatically generated from your criteria for
            developers to review and debug.
          </Typography>
        </Box>
      </Collapse>
    </Card>
  );
};

const tabToMatchType = (tab: string): CohortMatchType =>
  tab === "matched" ? "exact" : "almost";

export function PreviewResults({
  previewResults,
  loadingMode,
  onExport,
  onLoadMore,
  onParticipantClick,
  type,
  cohortName,
  studyId,
  groupId,
}: PreviewResultsProps) {
  const [selectedTab, setSelectedTab] = useState<string>("matched");
  const [selectedParticipants, setSelectedParticipants] = useState<Set<string>>(
    new Set()
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCohortModalOpen, setIsCohortModalOpen] = useState(false);

  const isReplacing = loadingMode === "replace";
  const isAppending = loadingMode === "append";

  const counts = previewResults?.counts;
  const page = previewResults?.page;
  const activeMatchType = tabToMatchType(selectedTab);
  const activeTotal =
    activeMatchType === "exact" ? counts?.exact ?? 0 : counts?.almost ?? 0;
  const activePageData: CohortParticipant[] = useMemo(
    () => (page && page.matchType === activeMatchType ? page.data : []),
    [page, activeMatchType]
  );
  const activeNextCursor =
    page && page.matchType === activeMatchType ? page.nextCursor : null;

  useEffect(() => {
    setSelectedParticipants(new Set());
  }, [
    previewResults?.cohortQueryId,
    previewResults?.cohortRunId,
    // Clear when parent nulls results (criteria edit / replace start).
    previewResults == null,
  ]);

  const handleTabChange = (_: any, newValue: string) => {
    setSelectedTab(newValue);
    const nextMatchType = tabToMatchType(newValue);
    if (!page || page.matchType !== nextMatchType) {
      onLoadMore(nextMatchType);
    }
  };

  const handleLoadMore = () => {
    if (!activeNextCursor) return;
    onLoadMore(activeMatchType, activeNextCursor);
  };

  const handleCheckboxToggle = (
    participantId: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.stopPropagation();
    setSelectedParticipants((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(participantId)) {
        newSet.delete(participantId);
      } else {
        newSet.add(participantId);
      }
      return newSet;
    });
  };

  const handleAddToStudyClick = () => {
    if (!previewResults?.cohortQueryId || !previewResults?.cohortRunId) {
      toastFn({
        title: "Save Required",
        description:
          "Please save the cohort before adding participants to study",
        variant: "warning",
      });
      return;
    }
    setIsModalOpen(true);
  };

  const handleAddToCohortClick = () => {
    if (!previewResults?.cohortQueryId || !previewResults?.cohortRunId) {
      toastFn({
        title: "Save Required",
        description:
          "Please save the cohort before adding participants to cohort",
        variant: "warning",
      });
      return;
    }
    setIsCohortModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCohortModalClose = () => {
    setIsCohortModalOpen(false);
  };

  const handleSelectAll = () => {
    const currentPageIds = activePageData.map((p) => p.id);
    if (currentPageIds.length === 0) return;

    const allSelected = currentPageIds.every((id) =>
      selectedParticipants.has(id)
    );

    setSelectedParticipants((prev) => {
      const newSet = new Set(prev);
      if (allSelected) {
        currentPageIds.forEach((id) => newSet.delete(id));
      } else {
        currentPageIds.forEach((id) => newSet.add(id));
      }
      return newSet;
    });
  };

  const handleExportSelected = () => {
    if (selectedParticipants.size > 0) {
      onExport(Array.from(selectedParticipants));
    }
  };

  const areAllSelected = useMemo(() => {
    if (activePageData.length === 0) return false;
    return activePageData.every((p) => selectedParticipants.has(p.id));
  }, [activePageData, selectedParticipants]);

  const selectedOnCurrentPage = useMemo(
    () => activePageData.filter((p) => selectedParticipants.has(p.id)).length,
    [activePageData, selectedParticipants]
  );

  return (
    <>
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: 3,
          boxShadow: "0px 1px 3px 0px rgba(0, 0, 0, 0.1)",
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: "#ffffff",
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: "rgba(0, 0, 0, 0.08)",
            bgcolor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h2" sx={{ color: "text.primary" }}>
              Preview Results
            </Typography>
            {selectedParticipants.size > 0 && (
              <Chip
                icon={
                  <CheckCircle
                    sx={{ fontSize: 16, color: "#ffffff !important" }}
                  />
                }
                label={`${selectedParticipants.size} selected`}
                size="small"
                sx={{
                  bgcolor: "primary.main",
                  color: "#ffffff",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  height: "24px",
                  borderRadius: "12px",
                }}
                data-testid="badge-selected"
              />
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            {selectedParticipants.size > 0 && (
              <>
                {type === "registry" && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddToStudyClick}
                    startIcon={<Add sx={{ fontSize: 18 }} />}
                    data-testid="button-add-to-study"
                  >
                    Add to Study
                  </Button>
                )}
                {type === "study" && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddToCohortClick}
                    startIcon={<Add sx={{ fontSize: 18 }} />}
                    data-testid="button-add-to-cohort"
                  >
                    Add to{" "}
                    <Typography
                      component="span"
                      title={cohortName || "Cohort"}
                      sx={{
                        maxWidth: 140,
                        display: "inline-block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "bottom",
                        ml: 0.5,
                        fontSize: "inherit",
                        fontWeight: "inherit",
                        lineHeight: "inherit",
                        color: "inherit",
                      }}
                    >
                      {cohortName || "Cohort"}
                    </Typography>
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportSelected}
                  startIcon={<Download sx={{ fontSize: 18 }} />}
                  data-testid="button-export-selected"
                >
                  Export Selected ({selectedParticipants.size})
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              size="small"
              disabled={!previewResults || (counts?.exact ?? 0) === 0}
              onClick={() => onExport()}
              startIcon={<Download sx={{ fontSize: 18 }} />}
              data-testid="button-export-csv"
            >
              Export All
            </Button>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <StyledTabs value={selectedTab} onChange={handleTabChange}>
              <StyledTab
                label={`Matched ${counts ? `(${counts.exact})` : ""}`}
                value="matched"
                data-testid="tab-matched"
                {...a11yProps(0)}
              />
              <StyledTab
                label={`Almost Eligible ${counts ? `(${counts.almost})` : ""}`}
                value="almost-eligible"
                data-testid="tab-almost-eligible"
                {...a11yProps(1)}
              />
            </StyledTabs>
          </Box>

          {(["matched", "almost-eligible"] as const).map((tabValue) => {
            const tabMatchType: CohortMatchType =
              tabValue === "matched" ? "exact" : "almost";
            const isActive = selectedTab === tabValue;
            const emptyLabel =
              tabValue === "matched"
                ? "No Exact matches found"
                : "No Almost Eligible matches found";
            return (
              <CustomTabPanel2
                key={tabValue}
                value={selectedTab}
                index={tabValue}
              >
                <Box sx={{ p: 3 }}>
                  {previewResults && !isReplacing && (
                    <PreviewHeader
                      count={
                        tabMatchType === "exact"
                          ? counts?.exact ?? 0
                          : counts?.almost ?? 0
                      }
                    />
                  )}

                  {isActive &&
                    !isReplacing &&
                    activePageData.length > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={areAllSelected}
                            onChange={handleSelectAll}
                          />
                        }
                        label={
                          <Typography variant="caption" color="text.secondary">
                            {areAllSelected ? "Deselect all" : "Select all"}{" "}
                            loaded
                          </Typography>
                        }
                      />
                      <Typography variant="caption" color="text.secondary">
                        {selectedOnCurrentPage} selected · showing{" "}
                        {activePageData.length} of {activeTotal}
                      </Typography>
                    </Box>
                  )}

                  {isActive &&
                    !isReplacing &&
                    !isAppending &&
                    previewResults &&
                    activeTotal === 0 && (
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 12,
                          color: "text.secondary",
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: "regular" }}
                        >
                          {emptyLabel}
                        </Typography>
                      </Box>
                    )}

                  {isActive && isReplacing && (
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {[1, 2, 3].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            height: 72,
                            bgcolor: "background.default",
                            borderRadius: "12px",
                            animation: "pulse 1.5s ease-in-out infinite",
                            "@keyframes pulse": {
                              "0%, 100%": { opacity: 1 },
                              "50%": { opacity: 0.5 },
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>

                {isActive && !isReplacing && (
                  <>
                    {activePageData.length > 0 && (
                      <VirtualizedParticipantList
                        participants={activePageData}
                        selectedParticipants={selectedParticipants}
                        handleCheckboxToggle={handleCheckboxToggle}
                        onParticipantClick={onParticipantClick}
                      />
                    )}

                    {activeNextCursor && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 2,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleLoadMore}
                          disabled={!!loadingMode}
                          startIcon={
                            isAppending ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : undefined
                          }
                          data-testid="preview-load-more"
                        >
                          {isAppending ? "Loading…" : "Load more"}
                        </Button>
                      </Box>
                    )}
                  </>
                )}
              </CustomTabPanel2>
            );
          })}
        </Box>

        {isModalOpen && (
          <AddToStudyModal
            open={isModalOpen}
            onClose={handleModalClose}
            selectedParticipants={selectedParticipants}
            cohortData={previewResults || undefined}
          />
        )}
        {isCohortModalOpen && (
          <AddToCohortModal
            open={isCohortModalOpen}
            onClose={handleCohortModalClose}
            selectedParticipants={selectedParticipants}
            cohortData={previewResults || undefined}
            cohortName={cohortName}
            studyId={studyId}
            groupId={groupId}
          />
        )}
      </Card>
      {!isReplacing && (
        <PreviewSQL
          sql={
            selectedTab === "matched"
              ? previewResults?.exactMatchSql || ""
              : previewResults?.almostMatchSql || ""
          }
          type={selectedTab === "matched" ? "Exact Matches" : "Almost Eligible"}
        />
      )}
    </>
  );
}
